import { Injectable } from '@angular/core';
import {
  getIdToken as fetchFirebaseIdToken,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from 'firebase/auth';
import { Observable, from } from 'rxjs';
import { MVP_DEMO_OFFLINE } from '../config/mvp-demo.config';
import { getFirebaseAuth } from '../firebase/firebase-app';
import type { AuthSessionUser } from '../models/auth-session-user.model';

const JWT_STORAGE_KEY = 'fition_admin_jwt';
const AUTH_USER_KEY = 'fition_admin_user';
const DEMO_SESSION_KEY = 'fition_admin_demo';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private authReady: Promise<void>;
  private resolveAuthReady!: () => void;

  constructor() {
    this.authReady = new Promise((resolve) => {
      this.resolveAuthReady = resolve;
    });

    if (MVP_DEMO_OFFLINE) {
      this.resolveAuthReady();
      return;
    }

    let settled = false;
    onAuthStateChanged(getFirebaseAuth(), () => {
      if (!settled) {
        settled = true;
        this.resolveAuthReady();
      }
    });
  }

  /** Espera a que Firebase restaure la sesión (p. ej. tras F5). */
  waitUntilReady(): Promise<void> {
    return this.authReady;
  }

  getToken(): string | null {
    return sessionStorage.getItem(JWT_STORAGE_KEY);
  }

  async getIdToken(): Promise<string | null> {
    if (MVP_DEMO_OFFLINE) {
      return this.getToken() ?? 'demo-token';
    }

    const auth = getFirebaseAuth();
    const user = auth.currentUser;
    if (!user) {
      return this.getToken();
    }

    try {
      const token = await fetchFirebaseIdToken(user);
      sessionStorage.setItem(JWT_STORAGE_KEY, token);
      return token;
    } catch {
      return null;
    }
  }

  getStoredUser(): AuthSessionUser | null {
    const raw = sessionStorage.getItem(AUTH_USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthSessionUser;
    } catch {
      return null;
    }
  }

  isLoggedIn(): boolean {
    if (MVP_DEMO_OFFLINE) {
      return sessionStorage.getItem(DEMO_SESSION_KEY) === '1';
    }
    return !!getFirebaseAuth().currentUser || !!this.getToken();
  }

  login(email: string, password: string): Observable<void> {
    if (MVP_DEMO_OFFLINE) {
      return from(
        Promise.resolve().then(() => this.setDemoSession(email.trim())),
      );
    }

    const auth = getFirebaseAuth();
    return from(
      signInWithEmailAndPassword(auth, email.trim(), password).then(
        async (credential) => {
          const token = await fetchFirebaseIdToken(credential.user);
          this.persistSession(token, credential.user);
        },
      ),
    );
  }

  async clear(): Promise<void> {
    sessionStorage.removeItem(JWT_STORAGE_KEY);
    sessionStorage.removeItem(AUTH_USER_KEY);
    sessionStorage.removeItem(DEMO_SESSION_KEY);
    if (!MVP_DEMO_OFFLINE) {
      try {
        await signOut(getFirebaseAuth());
      } catch {
        /* ya sin sesión */
      }
    }
  }

  private persistSession(token: string, user: User): void {
    sessionStorage.setItem(JWT_STORAGE_KEY, token);
    sessionStorage.setItem(
      AUTH_USER_KEY,
      JSON.stringify(this.userToSession(user)),
    );
  }

  private setDemoSession(email: string): void {
    sessionStorage.setItem(DEMO_SESSION_KEY, '1');
    sessionStorage.setItem(JWT_STORAGE_KEY, 'demo-token');
    sessionStorage.setItem(
      AUTH_USER_KEY,
      JSON.stringify({
        uid: 'demo',
        email,
        displayName: null,
      } satisfies AuthSessionUser),
    );
  }

  private userToSession(user: User): AuthSessionUser {
    return {
      uid: user.uid,
      email: user.email ?? '',
      displayName: user.displayName,
    };
  }
}
